/* assets/littleg-kid-handoffs.js — LITTLEG-FRIEND-MAP-HANDOFFS-14
 * External/public Around-station handoff owner for /pages/kid-map.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Map canvas, UI cards & markers: assets/littleg-kid-map.js
 * - Read-only UI state & marker palette: assets/littleg-kid-active-state.js (V1.12.0)
 * - Location privacy transformation: assets/littleg-kid-location-privacy.js (V1.3.0)
 * - Device-local profile identity: assets/littleg-profile.js (V1.1.0)
 * - Family social graph persistence: LittleGFamilyData.socialGraph
 * - Liquid DOM owner: sections/littleg-kid-map-workspace.liquid
 * - THIS FILE OWNS EXCLUSIVELY:
 *   1. Verified GCityPartners badge decoration.
 *   2. Explicit Around-station UI state toggling & unsupported public POI search handoffs (Google Maps).
 * - Map Passport is a separate canonical page; this runtime owns no Passport panel, reward, checkout, or parent drawer.
 *
 * PRIVACY GUARANTEE
 * Friend names, home addresses, station IDs, and exact coordinates never enter handoff URLs or external searches.
 */
(function (window, document) {
  'use strict';

  var VERSION = '1.10.0';
  var ROOT_SELECTOR = '[data-littleg-kid-map]';

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }
  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function clean(value, max) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f<>"'`]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max || 160);
  }

  function isChinese() {
    return /^zh\b/i.test(document.documentElement.lang || '');
  }

  function socialGraph() {
    var familyData = window.LittleGFamilyData;
    var graph = familyData && familyData.socialGraph;
    return graph && typeof graph.listPlaces === 'function' && typeof graph.getPlace === 'function'
      ? graph
      : null;
  }

  function confirmed(row) {
    return (
      !!row &&
      row.privacyConfirmed === true &&
      row.parentConfirmed === true &&
      row.approvalStatus === 'approved'
    );
  }

  function rowById(id) {
    var key = clean(id, 120);
    var graph = socialGraph();
    if (!key || !graph) return null;
    var row = graph.getPlace(key);
    return row &&
      typeof row === 'object' &&
      clean(row.visibility, 40) === 'family_private' &&
      confirmed(row)
      ? row
      : null;
  }

  function currentRow(root) {
    if (!root) return null;
    return rowById(root.__kidOpenSocialId || root.getAttribute('data-kid-selected-social-id'));
  }

  function stationName(row) {
    if (!row || clean(row.locationType || row.location_type, 40) !== 'station_anchor') return '';
    return clean(
      isChinese() ? row.stationNameZhHk || row.stationName : row.stationName || row.stationNameZhHk,
      120
    );
  }

  function safeOpen(url) {
    if (!url) return false;
    var opened = window.open(url, '_blank', 'noopener');
    if (!opened) window.location.href = url;
    return true;
  }

  function publicMapSearchUrl(term, row) {
    var station = stationName(row);
    if (!station) return '';
    return (
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(term + ' near ' + station + ' MTR Station Hong Kong')
    );
  }

  function safePartnerUrl(value) {
    var raw = clean(value, 500);
    if (!raw) return '';
    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      if (url.pathname !== '/pages/gcitypartners') return '';
      return url.pathname + url.search + url.hash;
    } catch (_) {
      return '';
    }
  }

  function decorateVerifiedPartners(root) {
    qa(root, '[data-partner-verified="true"][data-partner-url]').forEach(function (node) {
      if (node.getAttribute('data-kid-partner-decorated') === 'true') return;
      var href = safePartnerUrl(node.getAttribute('data-partner-url'));
      if (!href) return;

      node.setAttribute('data-kid-partner-decorated', 'true');
      var badge = document.createElement('a');
      badge.className = 'kid-verified-partner-stamp';
      badge.href = href;
      badge.setAttribute('data-kid-verified-partner-stamp', '');
      badge.textContent = '✓ GCityPartners Partner';
      node.appendChild(badge);
    });
  }

  function closeExplicitNearby(root) {
    if (!(root instanceof HTMLElement)) return;

    root.removeAttribute('data-kid-nearby-explicit');

    var details = q(root, '[data-kid-nearby-details]');
    if (details && details.open) details.open = false;

    var entry = q(root, '[data-kid-nearby-entry]');
    if (entry) entry.setAttribute('aria-expanded', 'false');

    root.dispatchEvent(new CustomEvent('littleg:kid-map:nearby-close'));
  }

  function openExplicitNearby(root) {
    if (!(root instanceof HTMLElement)) return;
    root.setAttribute('data-kid-nearby-explicit', 'true');

    var details = q(root, '[data-kid-nearby-details]');
    if (details) details.open = true;

    var entry = q(root, '[data-kid-nearby-entry]');
    if (entry) entry.setAttribute('aria-expanded', 'true');

    var card = q(root, '[data-kid-friend-card]');

    if (card && !card.hidden) {
      card.hidden = true;
      root.removeAttribute('data-kid-friend-card-open');

      var more = q(card, '[data-kid-friend-more]');
      if (more) more.hidden = true;

      var kidMap = window.LittleGKidMap;

      if (kidMap && typeof kidMap.syncFriendCardPadding === 'function') {
        kidMap.syncFriendCardPadding();
      }
    }
  }

  function bindExplicitNearbyState(root) {
    if (!root || root.__kidExplicitNearbyBound) return;
    root.__kidExplicitNearbyBound = true;

    root.addEventListener('gproperty:map:blank-click', function () {
      closeExplicitNearby(root);
    });

    var details = q(root, '[data-kid-nearby-details]');
    if (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) closeExplicitNearby(root);
      });
    }

    root.addEventListener('click', function (event) {
      var entry = event.target.closest('[data-kid-nearby-entry]');

      if (entry) {
        if (root.getAttribute('data-kid-nearby-explicit') === 'true') {
          closeExplicitNearby(root);
        } else {
          openExplicitNearby(root);
        }
        return;
      }

      var nearbyBtn = event.target.closest('[data-kid-friend-card] [data-kid-nearby-open]');
      if (!nearbyBtn) return;

      var row = currentRow(root);
      if (!row || !stationName(row)) return;

      openExplicitNearby(root);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (root.getAttribute('data-kid-nearby-explicit') !== 'true') return;

      var target = event.target.closest('[data-kid-nearby-details], [data-kid-nearby-entry]');

      if (!target) return;

      event.preventDefault();
      event.stopPropagation();

      closeExplicitNearby(root);

      var entry = q(root, '[data-kid-nearby-entry]');
      if (entry) entry.focus();
    });
  }

  function observeUnsupportedNearbyHandoffs(root) {
    if (!root || root.__kidPublicPoiHandoffObserver || typeof MutationObserver !== 'function')
      return;

    var terms = {
      attractions: 'attractions museums landmarks',
      treats: 'restaurants cafes dessert',
    };
    var lastKey = '';

    function project() {
      if (root.getAttribute('data-kid-nearby-explicit') !== 'true') return;
      var kind = clean(root.getAttribute('data-kid-active-filter'), 30);
      if (!terms[kind]) return;

      var row = currentRow(root);
      if (!row || !stationName(row)) return;

      var key = kind + '|' + clean(row.id, 120);
      if (key === lastKey) return;
      lastKey = key;

      safeOpen(publicMapSearchUrl(terms[kind], row));
    }

    var observer = new MutationObserver(function (records) {
      if (
        records.some(function (record) {
          return record.attributeName === 'data-kid-active-filter';
        })
      ) {
        project();
      }
    });

    observer.observe(root, { attributes: true, attributeFilter: ['data-kid-active-filter'] });
    root.__kidPublicPoiHandoffObserver = observer;
  }

  function observePartnerNodes(root) {
    if (!root || root.__kidPartnerObserver || typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function () {
      decorateVerifiedPartners(root);
    });

    observer.observe(root, { childList: true, subtree: true });
    root.__kidPartnerObserver = observer;
  }

  function mountRoot(root) {
    if (!root || root.__kidHandoffsVersion === VERSION) return;
    root.__kidHandoffsVersion = VERSION;

    decorateVerifiedPartners(root);
    observePartnerNodes(root);
    bindExplicitNearbyState(root);
    observeUnsupportedNearbyHandoffs(root);
  }

  function mountAll() {
    qa(document, ROOT_SELECTOR).forEach(mountRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', mountAll);

  window.LittleGKidParentHandoffs = Object.freeze({
    version: VERSION,
    mount: mountAll,
    privacy: 'station-anchor-or-legacy-approximate',
    socialGraphOwner: 'LittleGFamilyData.socialGraph',
    partnerPolicy: 'verified-only-no-inference',
    aroundStateOwner: true,
    publicPoiHandoffOwner: true,
    friendUtilityContract: 'around-state-and-public-poi-handoffs',
  });
})(window, document);
